import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import FarmersNav from "@/components/FarmersNav";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";
import {
  ArrowLeft, ArrowRight, UsersThree, ChatCircleText, Plus, MagnifyingGlass,
  ArrowFatUp, CheckCircle, Sparkle, MapPin, Camera, Trash,
} from "@phosphor-icons/react";

/**
 * FarmersCommunity — /farmers/community
 * Ask a farmer Q&A. Question list + inline question form + Q detail.
 */
export default function FarmersCommunity() {
  const { user, requireSignIn } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [unanswered, setUnanswered] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [allowedTags, setAllowedTags] = useState([]);
  const [tagCounts, setTagCounts] = useState([]);

  useEffect(() => {
    http.get("/farmers/questions/tags").then(({ data }) => {
      setAllowedTags(data.allowed || []);
      setTagCounts(data.tags || []);
    }).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (tag) params.set("tag", tag);
    if (unanswered) params.set("unanswered", "1");
    params.set("limit", "40");
    http.get(`/farmers/questions?${params.toString()}`)
      .then(({ data }) => setQuestions(data.questions || []))
      .catch(() => toast.error("Could not load questions"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [q, tag, unanswered]);

  const hero = "Ask a farmer. Learn from farmers. Some of the most valuable agricultural knowledge never appears in a textbook. It passes from one farmer to another. This is where that knowledge lives online.";

  const upvote = async (qid) => {
    if (!user) return requireSignIn("upvote this question");
    try {
      const { data } = await http.post(`/farmers/questions/${qid}/upvote`);
      setQuestions((qs) => qs.map((x) => x.id === qid ? { ...x, upvotes: data.upvotes, you_upvoted: data.upvoted } : x));
    } catch { toast.error("Could not upvote"); }
  };

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332] min-h-screen pt-32 md:pt-36 pb-32" data-testid="farmers-community-page">
      <FarmersNav />
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 mt-6">
        <Link to="/farmers" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <header className="mt-3">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
            <UsersThree size={12} weight="fill" /> / Farmer Community
          </div>
          <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">Ask a farmer. Learn from farmers.</h1>
          <p className="mt-2 font-serif-display italic text-[#3A3830] max-w-3xl">
            Q&A, discussions and farmer-to-farmer knowledge. Share a photo, ask in your language, get answers from your region and around the world.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <ListenTranslateBar text={hero} testidPrefix="farmers-community-tools" />
            <ShareButton title="Ask a Farmer — NaturePulse" text="Ask a photo question. Get answers from farmers everywhere." hashtags={["FarmersPulse"]} variant="chip" testid="farmers-community-share" />
            <button
              onClick={() => (user ? setShowForm(true) : requireSignIn("post a question"))}
              data-testid="farmers-community-ask-cta"
              className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
            >
              <Plus size={11} weight="bold" /> Ask a farmer
            </button>
          </div>
        </header>

        {/* Filters */}
        <div className="mt-6 flex items-center gap-2 rounded-lg border border-black/15 bg-white px-3 py-2 max-w-lg" data-testid="farmers-community-search">
          <MagnifyingGlass size={13} weight="bold" className="text-[#3A3830]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search questions in any language…"
            data-testid="farmers-community-search-input"
            className="flex-1 bg-transparent text-sm outline-none"
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="farmers-community-filters">
          <button onClick={() => setTag("")} data-testid="community-tag-all" className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase ${tag === "" ? "bg-[#1B4332] text-[#F5EBDC] border-black" : "bg-white border-black/15 hover:border-black/40"}`}>All</button>
          {(tagCounts.length ? tagCounts.map((t) => t.tag) : allowedTags.slice(0, 12)).map((t) => (
            <button
              key={t}
              onClick={() => setTag(tag === t ? "" : t)}
              data-testid={`community-tag-${t}`}
              className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase ${tag === t ? "bg-[#1B4332] text-[#F5EBDC] border-black" : "bg-white border-black/15 hover:border-black/40"}`}
            >
              {t}
            </button>
          ))}
          <label className="ml-2 inline-flex items-center gap-1.5 text-sm text-[#3A3830]">
            <input type="checkbox" checked={unanswered} onChange={(e) => setUnanswered(e.target.checked)} data-testid="community-unanswered" />
            Unanswered only
          </label>
        </div>

        {/* Question form (inline) */}
        {showForm && (
          <AskForm
            onClose={() => setShowForm(false)}
            allowedTags={allowedTags}
            onCreated={() => { setShowForm(false); load(); }}
          />
        )}

        {/* Questions */}
        <section className="mt-6 space-y-3" data-testid="farmers-community-list">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-24 rounded-2xl bg-white/60 animate-pulse" />)
          ) : questions.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-black/20 bg-white/70 p-10 text-center">
              <ChatCircleText size={22} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
              <div className="font-serif-display text-2xl">No questions here yet.</div>
              <p className="mt-1 text-sm text-[#3A3830]">Be the first to ask — every question helps another farmer.</p>
            </div>
          ) : (
            questions.map((qq) => (
              <QuestionCard key={qq.id} q={qq} onUpvote={() => upvote(qq.id)} />
            ))
          )}
        </section>
      </div>
    </main>
  );
}


// =====================================================================
//  QuestionCard
// =====================================================================
function QuestionCard({ q, onUpvote }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4 hover:border-[color:var(--np-brand,#B85F2A)]" data-testid={`community-question-${q.id}`}>
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-center gap-1 shrink-0">
          <button
            onClick={onUpvote}
            data-testid={`community-question-upvote-${q.id}`}
            className={`h-10 w-10 rounded-full border flex items-center justify-center transition ${q.you_upvoted ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent" : "bg-white border-black/15 hover:border-[color:var(--np-brand,#B85F2A)]"}`}
            aria-label="Upvote"
          >
            <ArrowFatUp size={16} weight="fill" />
          </button>
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">{q.upvotes || 0}</div>
        </div>
        <div className="flex-1 min-w-0">
          <Link to={`/farmers/community/${q.slug || q.id}`} className="block">
            <div className="font-serif-display text-lg md:text-xl leading-tight line-clamp-2 hover:text-[color:var(--np-brand,#B85F2A)]">
              {q.title}
            </div>
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-[#3A3830]">
            <span className="font-mono-tech tracking-[0.22em] uppercase">{q.owner_name}</span>
            <span>·</span>
            <span>{new Date(q.created_at).toLocaleDateString()}</span>
            {q.crop && <><span>·</span><span className="capitalize">{q.crop}</span></>}
            {q.location?.village && <><span>·</span><MapPin size={10} weight="fill" /> {q.location.village}</>}
            {q.answer_count > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase">
                <ChatCircleText size={9} weight="fill" /> {q.answer_count} answers
              </span>
            )}
            {q.accepted_answer_id && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase">
                <CheckCircle size={9} weight="fill" /> Solved
              </span>
            )}
          </div>
          {q.body && <p className="mt-2 text-sm text-[#3A3830] line-clamp-2">{q.body}</p>}
          {(q.tags || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {q.tags.slice(0, 4).map((t) => (
                <span key={t} className="rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase">
                  #{t}
                </span>
              ))}
            </div>
          )}
        </div>
        {q.photos?.[0] && (
          <img src={q.photos[0]} alt="" className="hidden sm:block h-20 w-20 rounded-lg object-cover border border-black/10" />
        )}
      </div>
    </div>
  );
}


// =====================================================================
//  Ask form (inline)
// =====================================================================
function AskForm({ onClose, allowedTags, onCreated }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [crop, setCrop] = useState("");
  const [tags, setTags] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [district, setDistrict] = useState("");
  const [village, setVillage] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleTag = (t) => setTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t].slice(0, 6));

  const uploadPhoto = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await http.post("/uploads/image", form, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data?.url;
      if (url) setPhotos((p) => [...p, url].slice(0, 6));
    } catch { toast.error("Photo upload failed"); }
  };

  const submit = async () => {
    if (title.trim().length < 8) return toast.error("Please write a clearer question title.");
    setBusy(true);
    try {
      await http.post("/farmers/questions", {
        title: title.trim(),
        body: body.trim() || undefined,
        crop: crop.trim() || undefined,
        tags,
        photos,
        location: { country: country.trim() || undefined, state: state.trim() || undefined, district: district.trim() || undefined, village: village.trim() || undefined },
      });
      toast.success("Question posted");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 rounded-2xl border border-black/15 bg-white p-5" data-testid="community-ask-form">
      <div className="flex items-center gap-2">
        <Sparkle size={14} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
        <div className="font-serif-display text-xl">Ask a farmer</div>
        <button onClick={onClose} className="ml-auto font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] hover:text-[#1B4332]">Cancel</button>
      </div>
      <label className="mt-3 block">
        <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Question *</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="community-ask-title" lang="auto" dir="auto"
               placeholder="e.g. Why are my tomato leaves turning yellow?"
               className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-base" />
      </label>
      <label className="mt-3 block">
        <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Details</span>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="community-ask-body" lang="auto" dir="auto" rows={4}
                  placeholder="Add context — variety, when it started, symptoms, weather, treatments tried…"
                  className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
      </label>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="block">
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Crop</span>
          <input value={crop} onChange={(e) => setCrop(e.target.value)} data-testid="community-ask-crop" lang="auto" dir="auto"
                 className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" placeholder="tomato" />
        </label>
        <label className="block">
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Country</span>
          <input value={country} onChange={(e) => setCountry(e.target.value)} data-testid="community-ask-country" lang="auto" dir="auto"
                 className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" placeholder="India" />
        </label>
        <label className="block">
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">State</span>
          <input value={state} onChange={(e) => setState(e.target.value)} data-testid="community-ask-state" lang="auto" dir="auto"
                 className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" placeholder="Kerala" />
        </label>
        <label className="block">
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Village / District</span>
          <input value={village || district} onChange={(e) => { setVillage(e.target.value); setDistrict(""); }} data-testid="community-ask-village" lang="auto" dir="auto"
                 className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
        </label>
      </div>
      <div className="mt-3">
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Tags (max 6)</div>
        <div className="mt-1 flex flex-wrap gap-1">
          {allowedTags.map((t) => (
            <button key={t} type="button" onClick={() => toggleTag(t)} data-testid={`community-tag-toggle-${t}`}
                    className={`rounded-full border px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase ${tags.includes(t) ? "bg-[#1B4332] text-[#F5EBDC] border-black" : "bg-white border-black/15 hover:border-black/40"}`}>#{t}</button>
          ))}
        </div>
      </div>
      <div className="mt-3">
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Photos ({photos.length}/6)</div>
        <div className="mt-2 flex flex-wrap gap-2 items-center">
          {photos.map((p, i) => (
            <div key={i} className="relative">
              <img src={p} alt="" className="h-16 w-16 rounded-lg object-cover border border-black/10" />
              <button onClick={() => setPhotos((x) => x.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-white border border-black/20 text-xs">×</button>
            </div>
          ))}
          {photos.length < 6 && (
            <label className="h-16 w-16 rounded-lg border border-dashed border-black/30 flex items-center justify-center cursor-pointer hover:bg-[#FBF2E1]" data-testid="community-ask-photo">
              <Camera size={16} weight="fill" className="text-[#3A3830]" />
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
            </label>
          )}
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button onClick={submit} disabled={busy} data-testid="community-ask-submit"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
          {busy ? "Posting…" : "Post question"}
          {!busy && <ArrowRight size={11} weight="bold" />}
        </button>
      </div>
    </div>
  );
}


// =====================================================================
//  QUESTION DETAIL
// =====================================================================
export function FarmersCommunityDetail() {
  const { qid } = useParams();
  const navigate = useNavigate();
  const { user, requireSignIn } = useAuth();
  const [question, setQuestion] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [photos, setPhotos] = useState([]);

  const load = () => {
    setLoading(true);
    http.get(`/farmers/questions/${encodeURIComponent(qid)}`)
      .then(({ data }) => { setQuestion(data.question); setAnswers(data.answers || []); })
      .catch(() => toast.error("Could not load question"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [qid]);

  const upvoteQ = async () => {
    if (!user) return requireSignIn("upvote this question");
    try {
      const { data } = await http.post(`/farmers/questions/${question.id}/upvote`);
      setQuestion((qq) => ({ ...qq, upvotes: data.upvotes, you_upvoted: data.upvoted }));
    } catch { toast.error("Could not upvote"); }
  };

  const upvoteA = async (aid) => {
    if (!user) return requireSignIn("upvote this answer");
    try {
      const { data } = await http.post(`/farmers/answers/${aid}/upvote`);
      setAnswers((as) => as.map((x) => x.id === aid ? { ...x, upvotes: data.upvotes, you_upvoted: data.upvoted } : x));
    } catch { toast.error("Could not upvote"); }
  };

  const accept = async (aid) => {
    try {
      await http.post(`/farmers/answers/${aid}/accept`);
      toast.success("Marked as accepted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not accept"); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await http.post("/uploads/image", form, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data?.url;
      if (url) setPhotos((p) => [...p, url].slice(0, 4));
    } catch { toast.error("Photo upload failed"); }
  };

  const submitAnswer = async () => {
    if (!user) return requireSignIn("post an answer");
    if (body.trim().length < 4) return toast.error("Write a longer answer.");
    setPosting(true);
    try {
      await http.post(`/farmers/questions/${question.id}/answers`, { body: body.trim(), photos });
      setBody(""); setPhotos([]);
      toast.success("Answer posted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post"); }
    finally { setPosting(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await http.delete(`/farmers/questions/${question.id}`);
      toast.success("Question deleted");
      navigate("/farmers/community");
    } catch { toast.error("Could not delete"); }
  };

  const listenText = useMemo(() => {
    if (!question) return "";
    const parts = [question.title, question.body || ""];
    for (const a of answers) parts.push(a.body);
    return parts.join(" \n\n ");
  }, [question, answers]);

  if (loading && !question) {
    return (
      <main className="bg-[color:var(--np-bg,#F5EBDC)] min-h-screen pt-32 md:pt-36 pb-32 px-5">
        <FarmersNav />
        <div className="max-w-[820px] mx-auto h-64 rounded-2xl bg-white/60 animate-pulse mt-8" />
      </main>
    );
  }
  if (!question) {
    return (
      <main className="bg-[color:var(--np-bg,#F5EBDC)] min-h-screen pt-32 md:pt-36 pb-32 px-5">
        <FarmersNav />
        <div className="max-w-[820px] mx-auto mt-8 text-center">
          <div className="font-serif-display text-2xl">Question not found.</div>
          <Link to="/farmers/community" className="mt-2 inline-block text-[color:var(--np-brand,#B85F2A)] underline">Back to Community</Link>
        </div>
      </main>
    );
  }

  const canManage = user && (user.firebase_uid === question.owner_uid || user.is_admin);

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332] min-h-screen pt-32 md:pt-36 pb-32" data-testid="farmers-community-detail-page">
      <FarmersNav />
      <div className="max-w-[820px] mx-auto px-5 md:px-8 mt-6">
        <Link to="/farmers/community" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]">
          <ArrowLeft size={11} weight="bold" /> Farmer Community
        </Link>
        <article className="mt-4 rounded-2xl border border-black/10 bg-white p-6" data-testid="community-question-detail">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            {question.owner_name} · {new Date(question.created_at).toLocaleDateString()}{question.crop ? ` · ${question.crop}` : ""}
          </div>
          <h1 className="mt-2 font-serif-display text-3xl md:text-4xl leading-tight" data-testid="community-question-title">{question.title}</h1>
          {question.body && <p className="mt-3 text-base whitespace-pre-wrap leading-relaxed text-[#2A2724]" data-testid="community-question-body">{question.body}</p>}
          {(question.tags || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {question.tags.map((t) => (
                <span key={t} className="rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase">#{t}</span>
              ))}
            </div>
          )}
          {(question.photos || []).length > 0 && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2">
              {question.photos.map((p, i) => <img key={i} src={p} alt="" className="w-full h-40 object-cover rounded-lg border border-black/10" />)}
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button onClick={upvoteQ} data-testid="community-question-upvote-detail"
                    className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase ${question.you_upvoted ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent" : "bg-white border-black/15 hover:border-[color:var(--np-brand,#B85F2A)]"}`}>
              <ArrowFatUp size={11} weight="fill" /> {question.upvotes || 0}
            </button>
            <ListenTranslateBar text={listenText} testidPrefix="community-detail-tools" />
            <ShareButton title={question.title} text={question.body || question.title} variant="chip" testid="community-question-share" />
            {canManage && (
              <button onClick={remove} data-testid="community-question-delete"
                      className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-50 text-rose-700 px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
                <Trash size={11} weight="fill" /> Delete
              </button>
            )}
          </div>
        </article>

        {/* Answers */}
        <section className="mt-6" data-testid="community-answers">
          <h2 className="font-serif-display text-2xl">
            {answers.length} {answers.length === 1 ? "answer" : "answers"}
          </h2>
          <div className="mt-3 space-y-3">
            {answers.map((a) => (
              <div key={a.id} className={`rounded-2xl border p-4 ${a.accepted ? "border-emerald-500 bg-emerald-50/40" : "border-black/10 bg-white"}`} data-testid={`community-answer-${a.id}`}>
                <div className="flex items-start gap-3">
                  <button onClick={() => upvoteA(a.id)} data-testid={`community-answer-upvote-${a.id}`}
                          className={`h-9 w-9 rounded-full border flex items-center justify-center ${a.you_upvoted ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent" : "bg-white border-black/15 hover:border-[color:var(--np-brand,#B85F2A)]"}`}>
                    <ArrowFatUp size={14} weight="fill" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#3A3830] font-mono-tech tracking-[0.22em] uppercase">
                      <span>{a.owner_name}</span>
                      {a.is_expert && <span className="rounded-full bg-blue-100 text-blue-800 border border-blue-200 px-1.5 py-0.5 text-[9px]">Expert</span>}
                      <span>·</span>
                      <span>{new Date(a.created_at).toLocaleDateString()}</span>
                      <span>·</span>
                      <span>{a.upvotes || 0} upvotes</span>
                      {a.accepted && <span className="ml-auto inline-flex items-center gap-1 text-emerald-800"><CheckCircle size={10} weight="fill" /> Accepted</span>}
                    </div>
                    <p className="mt-1 text-sm text-[#2A2724] whitespace-pre-wrap leading-relaxed" data-testid={`community-answer-body-${a.id}`}>{a.body}</p>
                    {(a.photos || []).length > 0 && (
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {a.photos.map((p, i) => <img key={i} src={p} alt="" className="h-24 w-24 object-cover rounded-lg border border-black/10" />)}
                      </div>
                    )}
                    {canManage && !a.accepted && (
                      <button onClick={() => accept(a.id)} data-testid={`community-answer-accept-${a.id}`}
                              className="mt-2 inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
                        <CheckCircle size={11} weight="fill" /> Accept
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Post answer */}
          <div className="mt-6 rounded-2xl border border-black/15 bg-white p-4" data-testid="community-answer-form">
            <div className="font-serif-display text-lg">Your answer</div>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} lang="auto" dir="auto"
                      data-testid="community-answer-input"
                      placeholder={user ? "Share what you know…" : "Sign in to post an answer"}
                      className="mt-2 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            {photos.length > 0 && (
              <div className="mt-2 flex gap-2 flex-wrap">
                {photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt="" className="h-16 w-16 object-cover rounded-lg border border-black/10" />
                    <button onClick={() => setPhotos((x) => x.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-white border border-black/20 text-xs">×</button>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase cursor-pointer hover:border-[color:var(--np-brand,#B85F2A)]" data-testid="community-answer-photo">
                <Camera size={11} weight="fill" /> Add photo
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
              </label>
              <button onClick={submitAnswer} disabled={posting || !user} data-testid="community-answer-submit"
                      className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
                {posting ? "Posting…" : (user ? "Post answer" : "Sign in to answer")}
                {!posting && user && <ArrowRight size={11} weight="bold" />}
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
